import React, { useMemo, useState } from 'react';
import { Box, Flex, Heading, Icon, Select, Spinner, Stack, Text, Tooltip } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Calendar } from 'phosphor-react';

import { GlassCard } from '../../../../../components/glass';
import { ICalendarioCelula, useFechamentos } from '../../../../../hooks/useFechamentos';
import { MESES_FULL } from '../helpers';
import { InfoPopover } from './InfoPopover';

const PERIODO_OPCOES = [
  { value: 1, label: '1 ano' },
  { value: 2, label: '2 anos' },
  { value: 3, label: '3 anos' },
  { value: 5, label: '5 anos' },
  { value: 0, label: 'Tudo' },
];

const MESES_LABELS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

// Cores: verde para "OK" (mês passado/atual) e gradiente vermelho para "pendente"
const COLOR_EMPTY = { bg: '#f3f4f6', text: '#9ca3af' };
const COLOR_OK = { bg: '#22c55e', text: '#ffffff' };
const COLOR_OK_LIGHT = { bg: '#bbf7d0', text: '#166534' };

const PENDENTE_LEVELS = [
  { threshold: 0.2, bg: '#fef3c7', text: '#854d0e' },
  { threshold: 0.4, bg: '#fed7aa', text: '#7c2d12' },
  { threshold: 0.6, bg: '#fb923c', text: '#7c2d12' },
  { threshold: 0.8, bg: '#ef4444', text: '#ffffff' },
  { threshold: 1.0, bg: '#b91c1c', text: '#ffffff' },
];

const getPendenteColor = (qtd: number, max: number): { bg: string; text: string } => {
  if (qtd === 0 || max === 0) return COLOR_EMPTY;
  const intensity = qtd / max;
  return (
    PENDENTE_LEVELS.find(l => intensity <= l.threshold) ??
    PENDENTE_LEVELS[PENDENTE_LEVELS.length - 1]
  );
};

export interface ICelulaSelecionada {
  ano: number;
  mes: number;
  qtd: number;
  ehOk: boolean;
}

interface CalendarioFechamentosProps {
  externalFilters?: Record<string, string | number | boolean | undefined>;
  selectedCelula?: ICelulaSelecionada | null;
  // eslint-disable-next-line no-unused-vars
  onSelectCelula?: (celula: ICelulaSelecionada | null) => void;
}

export const CalendarioFechamentos = ({
  externalFilters = {},
  selectedCelula = null,
  onSelectCelula,
}: CalendarioFechamentosProps) => {
  const { getCalendario } = useFechamentos();

  const [anosAtras, setAnosAtras] = useState<number>(2);

  const queryFilters = useMemo(
    () => ({ ...externalFilters, anos_atras: anosAtras }),
    [externalFilters, anosAtras],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['fechamento-calendario', queryFilters],
    queryFn: getCalendario,
    staleTime: 5 * 60 * 1000,
    // Mantem grid anterior visivel durante refetch (troca de periodo/filtro)
    // — evita o flicker de toda a tabela sumir e voltar.
    placeholderData: prev => prev,
  });

  const calendario = data?.data;

  const celulasMap = useMemo(() => {
    const m = new Map<string, ICalendarioCelula>();
    calendario?.celulas.forEach(c => m.set(`${c.ano}-${c.mes}`, c));
    return m;
  }, [calendario]);

  // Memoizado: o componente renderiza ~144 celulas e cada uma chama isMesOk.
  // Se anoAtual/mesAtual fossem recalculados a cada render, perderia valor o memo.
  const { anoAtual, mesAtual } = useMemo(() => {
    const hoje = new Date();
    return { anoAtual: hoje.getFullYear(), mesAtual: hoje.getMonth() + 1 };
  }, []);

  // Mes "OK" = mes atual ou mes anterior (no calendario gregoriano).
  // Calcula explicitamente o ano/mes do mes anterior pra evitar logica em
  // cascata por ano (era fragil em janeiro).
  const isMesOk = (ano: number, mes: number): boolean => {
    if (ano === anoAtual && mes === mesAtual) return true;
    // Mes anterior: subtrai 1 com wraparound dezembro/ano anterior.
    const mesAnterior = mesAtual === 1 ? 12 : mesAtual - 1;
    const anoAnterior = mesAtual === 1 ? anoAtual - 1 : anoAtual;
    return ano === anoAnterior && mes === mesAnterior;
  };

  const anos = useMemo(() => {
    // "Tudo" — usa os anos retornados pelo backend (do mais antigo ate o atual)
    if (anosAtras === 0) {
      const fromBackend = calendario?.anos ?? [];
      if (fromBackend.length === 0) return [anoAtual];
      const minAno = Math.min(...fromBackend);
      const arr: number[] = [];
      for (let a = minAno; a <= anoAtual; a += 1) arr.push(a);
      return arr;
    }
    const arr: number[] = [];
    for (let a = anoAtual - anosAtras; a <= anoAtual; a += 1) arr.push(a);
    return arr;
  }, [anoAtual, anosAtras, calendario?.anos]);

  const qtdMax = calendario?.qtd_max ?? 0;
  const totalEmpresas = calendario?.total_empresas ?? 0;

  return (
    <GlassCard intensity="medium" p={6}>
      <Flex
        align={{ base: 'stretch', md: 'center' }}
        justify="space-between"
        gap={4}
        mb={4}
        direction={{ base: 'column', md: 'row' }}
      >
        <Box>
          <Heading size="md" color="glass.text">
            <Flex align="center" gap={2}>
              <Icon as={Calendar} />
              Calendário de fechamentos contábeis
              <InfoPopover title="Calendário anual">
                Cada célula mostra a{' '}
                <strong>
                  quantidade de empresas cujo último fechamento contábil foi naquele mês
                </strong>
                . Células do mês atual e mês passado ficam verdes (clientes em dia). Meses
                anteriores ficam em gradiente vermelho — quanto mais empresas paradas no passado,
                mais quente. Clique numa célula para filtrar a tabela com essas empresas.
              </InfoPopover>
            </Flex>
          </Heading>
        </Box>

        <Flex align="center" gap={2}>
          <Text fontSize="sm" color="gray.500">
            Período:
          </Text>
          <Select
            size="sm"
            maxW="120px"
            value={anosAtras}
            onChange={e => setAnosAtras(Number(e.target.value))}
          >
            {PERIODO_OPCOES.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Flex>
      </Flex>

      {isLoading && (
        <Flex justify="center" py={16}>
          <Spinner size="lg" color="blue.500" />
        </Flex>
      )}

      {!isLoading && qtdMax === 0 && (
        <Flex justify="center" py={16}>
          <Stack align="center" spacing={2}>
            <Icon as={Calendar} color="gray.300" boxSize={10} />
            <Text color="gray.500" fontSize="sm">
              Nenhum fechamento contábil registrado no período.
            </Text>
          </Stack>
        </Flex>
      )}

      {!isLoading && qtdMax > 0 && (
        <>
          <Box display="grid" gridTemplateColumns="64px repeat(12, 1fr)" gap="6px" mb={4}>
            <Box />
            {MESES_LABELS.map((m, idx) => (
              <Text
                key={m}
                fontSize="11px"
                fontWeight="semibold"
                color="gray.500"
                textAlign="center"
                pb={1}
                borderBottomWidth={mesAtual === idx + 1 ? '2px' : '0'}
                borderBottomColor="blue.400"
              >
                {m}
              </Text>
            ))}

            {anos.map(ano => (
              <React.Fragment key={ano}>
                <Flex
                  align="center"
                  justify="center"
                  fontSize="13px"
                  fontWeight="bold"
                  color="gray.600"
                >
                  {ano}
                </Flex>
                {MESES_LABELS.map((_label, idx) => {
                  const mes = idx + 1;
                  const cel = celulasMap.get(`${ano}-${mes}`);
                  const qtd = cel?.qtd ?? 0;
                  const isFuture = ano > anoAtual || (ano === anoAtual && mes > mesAtual);
                  const isCurrent = ano === anoAtual && mes === mesAtual;
                  const ehOk = isMesOk(ano, mes);
                  const isSelected =
                    !!selectedCelula && selectedCelula.ano === ano && selectedCelula.mes === mes;
                  const isClickable = !isFuture && qtd > 0 && !!onSelectCelula;

                  let colors = COLOR_EMPTY;
                  if (qtd > 0) {
                    if (ehOk) {
                      // OK: verde com intensidade pela qtd
                      colors = qtd >= qtdMax * 0.4 ? COLOR_OK : COLOR_OK_LIGHT;
                    } else {
                      colors = getPendenteColor(qtd, qtdMax);
                    }
                  }

                  let cellLabel = '';
                  if (isFuture) cellLabel = '·';
                  else if (qtd > 0) cellLabel = String(qtd);

                  let borderWidth = '1px';
                  let borderColor = 'gray.100';
                  if (isSelected) {
                    borderWidth = '3px';
                    borderColor = 'blue.600';
                  } else if (isCurrent) {
                    borderWidth = '2px';
                    borderColor = 'blue.400';
                  }

                  const handleClick = () => {
                    if (!isClickable) return;
                    if (isSelected) {
                      onSelectCelula(null);
                    } else {
                      onSelectCelula({ ano, mes, qtd, ehOk });
                    }
                  };

                  let cellCursor: 'default' | 'pointer' = 'default';
                  if (isClickable) cellCursor = 'pointer';

                  return (
                    <Tooltip
                      key={`${ano}-${mes}`}
                      label={
                        isFuture ? (
                          <Text fontSize="xs">Mês futuro</Text>
                        ) : (
                          <CelulaTooltipContent
                            ano={ano}
                            mes={mes}
                            qtd={qtd}
                            ehOk={ehOk}
                            amostra={cel?.amostra ?? []}
                            isClickable={isClickable}
                            isSelected={isSelected}
                          />
                        )
                      }
                      placement="top"
                      hasArrow
                      bg="gray.800"
                      maxW="320px"
                    >
                      <Box
                        bg={isFuture ? 'transparent' : colors.bg}
                        color={colors.text}
                        borderWidth={borderWidth}
                        borderColor={borderColor}
                        borderRadius="md"
                        h="48px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="14px"
                        fontWeight="semibold"
                        cursor={cellCursor}
                        opacity={isFuture ? 0.3 : 1}
                        transition="all 0.15s"
                        boxShadow={isSelected ? 'md' : undefined}
                        onClick={handleClick}
                        role={isClickable ? 'button' : undefined}
                        tabIndex={isClickable ? 0 : undefined}
                        aria-pressed={isClickable ? isSelected : undefined}
                        aria-label={
                          isClickable
                            ? `${ano}/${String(mes).padStart(2, '0')} — ${qtd} empresa${qtd === 1 ? '' : 's'}, ${ehOk ? 'em dia' : 'pendente'}`
                            : undefined
                        }
                        onKeyDown={
                          isClickable
                            ? e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleClick();
                                }
                              }
                            : undefined
                        }
                        _hover={
                          isClickable
                            ? {
                                transform: 'scale(1.08)',
                                boxShadow: 'md',
                                zIndex: 2,
                              }
                            : undefined
                        }
                      >
                        {cellLabel}
                      </Box>
                    </Tooltip>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>

          <Flex
            justify="space-between"
            align="center"
            mt={4}
            pt={3}
            borderTopWidth="1px"
            borderColor="gray.100"
            wrap="wrap"
            gap={2}
          >
            <Flex align="center" gap={3} wrap="wrap">
              <Flex align="center" gap={1.5}>
                <Box w="14px" h="14px" bg={COLOR_OK.bg} borderRadius="sm" />
                <Text fontSize="xs" color="glass.textMuted">
                  Em dia (mês passado/atual)
                </Text>
              </Flex>
              <Flex align="center" gap={1}>
                {PENDENTE_LEVELS.map(level => (
                  <Box
                    key={level.threshold}
                    w="14px"
                    h="14px"
                    bg={level.bg}
                    borderRadius="sm"
                    borderWidth="1px"
                    borderColor="gray.200"
                  />
                ))}
                <Text fontSize="xs" color="glass.textMuted" ml={1}>
                  Pendentes (mais escuro = mais empresas)
                </Text>
              </Flex>
            </Flex>
            <Text fontSize="xs" color="glass.textMuted">
              Total: <strong>{totalEmpresas}</strong> empresas · pico: <strong>{qtdMax}</strong> em
              um único mês
            </Text>
          </Flex>
        </>
      )}
    </GlassCard>
  );
};

const CelulaTooltipContent = ({
  ano,
  mes,
  qtd,
  ehOk,
  amostra,
  isClickable = false,
  isSelected = false,
}: {
  ano: number;
  mes: number;
  qtd: number;
  ehOk: boolean;
  amostra: { codi_emp: string | null; nome: string | null }[];
  isClickable?: boolean;
  isSelected?: boolean;
}) => (
  <Box py={1}>
    <Text fontSize="sm" fontWeight="bold" mb={0.5}>
      {MESES_FULL[mes - 1]} de {ano}
    </Text>
    <Text fontSize="xs" color={ehOk ? 'green.200' : 'orange.200'} mb={2}>
      {qtd === 0
        ? 'Nenhuma empresa parou aqui'
        : `${qtd} empresa${qtd > 1 ? 's' : ''} ${ehOk ? 'em dia' : 'paradas nesse mês'}`}
    </Text>
    {amostra.length > 0 && (
      <>
        <Text fontSize="10px" color="gray.400" textTransform="uppercase" mb={1}>
          Amostra
        </Text>
        <Stack spacing={0.5}>
          {amostra.map(emp => (
            <Text key={emp.codi_emp} fontSize="xs" color="white" isTruncated>
              {emp.codi_emp ? `[${emp.codi_emp}] ` : ''}
              {emp.nome || '(sem nome)'}
            </Text>
          ))}
          {qtd > amostra.length && (
            <Text fontSize="10px" color="gray.400" mt={1}>
              + {qtd - amostra.length} outras...
            </Text>
          )}
        </Stack>
      </>
    )}
    {isClickable && (
      <Text fontSize="10px" color="blue.200" mt={2} fontStyle="italic">
        {isSelected ? 'Clique para limpar o filtro' : 'Clique para filtrar a tabela abaixo'}
      </Text>
    )}
  </Box>
);
